import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import DashboardHeader from '../../components/common/DashboardHeader';
import {
  CogIcon,
  BellIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  KeyIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface Settings {
  platform: {
    name: string;
    logo_url: string;
    timezone: string;
    default_language: string;
    maintenance_mode: boolean;
  };
  notifications: {
    email_enabled: boolean;
    sms_enabled: boolean;
    whatsapp_enabled: boolean;
    push_enabled: boolean;
  };
  payment: {
    tap_payments_enabled: boolean;
    commission_rate: number;
    processing_fee: number;
    auto_payment_retry: boolean;
  };
  security: {
    two_factor_required: boolean;
    password_expiry_days: number;
    max_login_attempts: number;
    session_timeout_minutes: number;
  };
  features: {
    garage_approval_required: boolean;
    customer_reviews_enabled: boolean;
    inventory_management: boolean;
    analytics_enabled: boolean;
  };
}

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('platform');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Mock data for now - replace with actual API call
      const mockSettings: Settings = {
        platform: {
          name: 'Rayyi7',
          logo_url: '/logo.png',
          timezone: 'Asia/Kuwait',
          default_language: 'en',
          maintenance_mode: false,
        },
        notifications: {
          email_enabled: true,
          sms_enabled: true,
          whatsapp_enabled: true,
          push_enabled: false,
        },
        payment: {
          tap_payments_enabled: true,
          commission_rate: 15.0,
          processing_fee: 2.5,
          auto_payment_retry: true,
        },
        security: {
          two_factor_required: false,
          password_expiry_days: 90,
          max_login_attempts: 5,
          session_timeout_minutes: 60,
        },
        features: {
          garage_approval_required: true,
          customer_reviews_enabled: true,
          inventory_management: true,
          analytics_enabled: true,
        },
      };
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      setSettings(mockSettings);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (category: string, data: any) => {
    try {
      setSaving(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success(`${category.charAt(0).toUpperCase() + category.slice(1)} settings updated successfully`);
      
      // Update local state to reflect changes
      if (settings) {
        setSettings({
          ...settings,
          [category]: data,
        });
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (category: keyof Settings, field: string, value: any) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      [category]: {
        ...settings[category],
        [field]: value,
      },
    });
  };

  const tabs = [
    { id: 'platform', label: 'Platform', icon: CogIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'payment', label: 'Payment', icon: CurrencyDollarIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'features', label: 'Features', icon: CheckCircleIcon },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
            <CogIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mr-2 sm:mr-3" />
            System Settings
          </h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">Configure platform settings and preferences</p>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <div className="overflow-x-auto">
              <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-3 sm:py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-[#008B8B] text-[#008B8B]'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6">
            {/* Platform Settings */}
            {activeTab === 'platform' && settings && (
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Platform Configuration</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Platform Name</label>
                    <input
                      type="text"
                      value={settings.platform.name}
                      onChange={(e) => handleInputChange('platform', 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Default Language</label>
                    <select
                      value={settings.platform.default_language}
                      onChange={(e) => handleInputChange('platform', 'default_language', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    >
                      <option value="en">English</option>
                      <option value="ar">Arabic</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                    <select
                      value={settings.platform.timezone}
                      onChange={(e) => handleInputChange('platform', 'timezone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    >
                      <option value="Asia/Kuwait">Kuwait (GMT+3)</option>
                      <option value="Asia/Riyadh">Saudi Arabia (GMT+3)</option>
                      <option value="Asia/Dubai">UAE (GMT+4)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.platform.maintenance_mode}
                        onChange={(e) => handleInputChange('platform', 'maintenance_mode', e.target.checked)}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Maintenance Mode</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">Enable to put platform in maintenance mode</p>
                  </div>
                </div>

                <button
                  onClick={() => updateSettings('platform', settings.platform)}
                  disabled={saving}
                  className="bg-[#008B8B] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#006666] disabled:opacity-50 text-sm sm:text-base"
                >
                  {saving ? 'Saving...' : 'Save Platform Settings'}
                </button>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && settings && (
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Notification Channels</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <EnvelopeIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Email Notifications</p>
                        <p className="text-xs text-gray-500">Send notifications via email for important updates</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleInputChange('notifications', 'email_enabled', !settings.notifications.email_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.notifications.email_enabled ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.notifications.email_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <DevicePhoneMobileIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">SMS Notifications</p>
                        <p className="text-xs text-gray-500">Send SMS for urgent notifications and OTP</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleInputChange('notifications', 'sms_enabled', !settings.notifications.sms_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.notifications.sms_enabled ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.notifications.sms_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.485 3.485"/>
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">WhatsApp Notifications</p>
                        <p className="text-xs text-gray-500">Send updates via WhatsApp Business API</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleInputChange('notifications', 'whatsapp_enabled', !settings.notifications.whatsapp_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.notifications.whatsapp_enabled ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.notifications.whatsapp_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center">
                      <BellIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Push Notifications</p>
                        <p className="text-xs text-gray-500">Browser and mobile app push notifications</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleInputChange('notifications', 'push_enabled', !settings.notifications.push_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.notifications.push_enabled ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.notifications.push_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Notification Preferences</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Templates Language</label>
                      <select
                        value={settings.platform.default_language}
                        onChange={(e) => handleInputChange('platform', 'default_language', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent text-sm"
                      >
                        <option value="en">English</option>
                        <option value="ar">Arabic</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Notification Frequency</label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent text-sm"
                        defaultValue="immediate"
                      >
                        <option value="immediate">Immediate</option>
                        <option value="hourly">Hourly Digest</option>
                        <option value="daily">Daily Summary</option>
                      </select>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => updateSettings('notifications', settings.notifications)}
                  disabled={saving}
                  className="bg-[#008B8B] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#006666] disabled:opacity-50 text-sm sm:text-base"
                >
                  {saving ? 'Saving...' : 'Save Notification Settings'}
                </button>
              </div>
            )}

            {/* Payment Settings */}
            {activeTab === 'payment' && settings && (
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Payment Configuration</h3>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-800">TAP Payments Integration</p>
                      <p className="text-xs text-blue-600 mt-1">Connected and active for processing payments</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Platform Commission Rate (%)
                      <span className="text-xs text-gray-500 block">Fee charged to garages per transaction</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="50"
                        value={settings.payment.commission_rate}
                        onChange={(e) => handleInputChange('payment', 'commission_rate', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                      />
                      <span className="absolute right-3 top-2 text-gray-500">%</span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Processing Fee (KWD)
                      <span className="text-xs text-gray-500 block">Fixed fee per transaction</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={settings.payment.processing_fee}
                        onChange={(e) => handleInputChange('payment', 'processing_fee', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                      />
                      <span className="absolute left-3 top-2 text-gray-500">KWD</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Partner Share (%)
                      <span className="text-xs text-gray-500 block">Revenue share with partner garages</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        defaultValue="40"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                      />
                      <span className="absolute right-3 top-2 text-gray-500">%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Settlement Period
                      <span className="text-xs text-gray-500 block">How often to transfer payments to garages</span>
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent">
                      <option value="daily">Daily</option>
                      <option value="weekly" selected>Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">TAP Payments Gateway</p>
                      <p className="text-xs text-gray-500">Enable TAP Payments for credit card processing</p>
                    </div>
                    <button
                      onClick={() => handleInputChange('payment', 'tap_payments_enabled', !settings.payment.tap_payments_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.payment.tap_payments_enabled ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.payment.tap_payments_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Auto Payment Retry</p>
                      <p className="text-xs text-gray-500">Automatically retry failed subscription payments</p>
                    </div>
                    <button
                      onClick={() => handleInputChange('payment', 'auto_payment_retry', !settings.payment.auto_payment_retry)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.payment.auto_payment_retry ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.payment.auto_payment_retry ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-yellow-800 mb-2">Revenue Calculator</h4>
                  <p className="text-xs text-yellow-700">
                    Based on current settings: For a 100 KWD transaction, the platform will receive{' '}
                    <strong>{(settings.payment.commission_rate + settings.payment.processing_fee).toFixed(2)} KWD</strong>{' '}
                    ({settings.payment.commission_rate}% + {settings.payment.processing_fee} KWD processing fee)
                  </p>
                </div>

                <button
                  onClick={() => updateSettings('payment', settings.payment)}
                  disabled={saving}
                  className="bg-[#008B8B] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#006666] disabled:opacity-50 text-sm sm:text-base"
                >
                  {saving ? 'Saving...' : 'Save Payment Settings'}
                </button>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && settings && (
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Security Configuration</h3>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <ShieldCheckIcon className="w-5 h-5 text-red-600 mr-2" />
                    <p className="text-sm font-medium text-red-800">Security Best Practices</p>
                  </div>
                  <p className="text-xs text-red-700 mt-1">Configure these settings carefully to maintain platform security while ensuring user accessibility.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password Expiry (Days)
                      <span className="text-xs text-gray-500 block">How often users must change passwords</span>
                    </label>
                    <input
                      type="number"
                      min="30"
                      max="365"
                      value={settings.security.password_expiry_days}
                      onChange={(e) => handleInputChange('security', 'password_expiry_days', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Recommended: 90-180 days</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Login Attempts
                      <span className="text-xs text-gray-500 block">Account locks after this many failed attempts</span>
                    </label>
                    <input
                      type="number"
                      min="3"
                      max="10"
                      value={settings.security.max_login_attempts}
                      onChange={(e) => handleInputChange('security', 'max_login_attempts', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Recommended: 5 attempts</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Session Timeout (Minutes)
                      <span className="text-xs text-gray-500 block">Auto-logout after inactivity</span>
                    </label>
                    <select
                      value={settings.security.session_timeout_minutes}
                      onChange={(e) => handleInputChange('security', 'session_timeout_minutes', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="120">2 hours</option>
                      <option value="480">8 hours</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Lockout Duration
                      <span className="text-xs text-gray-500 block">How long accounts stay locked</span>
                    </label>
                    <select
                      defaultValue="30"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                    >
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="1440">24 hours</option>
                      <option value="manual">Manual unlock only</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Two-Factor Authentication Required</p>
                      <p className="text-xs text-gray-500">Require 2FA for all admin and garage owner accounts</p>
                    </div>
                    <button
                      onClick={() => handleInputChange('security', 'two_factor_required', !settings.security.two_factor_required)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.security.two_factor_required ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.security.two_factor_required ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">IP Whitelist for Admin</p>
                      <p className="text-xs text-gray-500">Restrict admin access to specific IP addresses</p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-gray-200`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">API Rate Limiting</p>
                      <p className="text-xs text-gray-500">Limit API requests per user per minute</p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-[#008B8B]`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6`} />
                    </button>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Password Requirements</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked className="mr-2" readOnly />
                        Minimum 8 characters
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked className="mr-2" readOnly />
                        At least one uppercase letter
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked className="mr-2" readOnly />
                        At least one lowercase letter
                      </label>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked className="mr-2" readOnly />
                        At least one number
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" checked className="mr-2" readOnly />
                        At least one special character
                      </label>
                      <label className="flex items-center text-sm">
                        <input type="checkbox" className="mr-2" readOnly />
                        No common passwords
                      </label>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => updateSettings('security', settings.security)}
                  disabled={saving}
                  className="bg-[#008B8B] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#006666] disabled:opacity-50 text-sm sm:text-base"
                >
                  {saving ? 'Saving...' : 'Save Security Settings'}
                </button>
              </div>
            )}

            {/* Feature Settings */}
            {activeTab === 'features' && settings && (
              <div className="space-y-4 sm:space-y-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">Platform Features</h3>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                    <p className="text-sm font-medium text-green-800">Feature Control Center</p>
                  </div>
                  <p className="text-xs text-green-700 mt-1">Enable or disable platform features to customize the user experience.</p>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Garage Approval Required</p>
                      <p className="text-xs text-gray-500">Require admin approval for new garage registrations</p>
                    </div>
                    <button
                      onClick={() => handleInputChange('features', 'garage_approval_required', !settings.features.garage_approval_required)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.features.garage_approval_required ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.features.garage_approval_required ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Customer Reviews & Ratings</p>
                      <p className="text-xs text-gray-500">Allow customers to rate and review garage services</p>
                    </div>
                    <button
                      onClick={() => handleInputChange('features', 'customer_reviews_enabled', !settings.features.customer_reviews_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.features.customer_reviews_enabled ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.features.customer_reviews_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Inventory Management</p>
                      <p className="text-xs text-gray-500">Enable inventory tracking and parts management</p>
                    </div>
                    <button
                      onClick={() => handleInputChange('features', 'inventory_management', !settings.features.inventory_management)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.features.inventory_management ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.features.inventory_management ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Advanced Analytics</p>
                      <p className="text-xs text-gray-500">Enable detailed analytics and business intelligence</p>
                    </div>
                    <button
                      onClick={() => handleInputChange('features', 'analytics_enabled', !settings.features.analytics_enabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        settings.features.analytics_enabled ? 'bg-[#008B8B]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          settings.features.analytics_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Mobile App Features</p>
                      <p className="text-xs text-gray-500">Enable mobile app exclusive features</p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-[#008B8B]`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Multi-Language Support</p>
                      <p className="text-xs text-gray-500">Support for Arabic and English languages</p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-[#008B8B]`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Loyalty Program</p>
                      <p className="text-xs text-gray-500">Customer loyalty points and rewards system</p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-gray-200`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1`} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-3 sm:p-4 border border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Appointment Booking</p>
                      <p className="text-xs text-gray-500">Online appointment scheduling system</p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors bg-[#008B8B]`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6`} />
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Feature Usage Statistics</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Reviews Posted</p>
                      <p className="text-lg font-bold text-gray-900">1,247</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Inventory Items</p>
                      <p className="text-lg font-bold text-gray-900">15,632</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Appointments Booked</p>
                      <p className="text-lg font-bold text-gray-900">3,891</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => updateSettings('features', settings.features)}
                  disabled={saving}
                  className="bg-[#008B8B] text-white px-4 sm:px-6 py-2 rounded-lg hover:bg-[#006666] disabled:opacity-50 text-sm sm:text-base"
                >
                  {saving ? 'Saving...' : 'Save Feature Settings'}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;